import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import AppDownloadModal from '@/components/shared/AppDownloadModal';
import LoadingSpinner from '@/components/shared/LoadingSpinner';
import { handleDeepLinkWithFallback } from '@/utils/deepLink';

/**
 * LivestreamRedirectPage
 * 
 * Handles redirects from mobile app livestream share links.
 * Attempts to open the mobile app, shows download modal if app is not installed.
 */
export default function LivestreamRedirectPage() {
  const [searchParams] = useSearchParams();
  const [showModal, setShowModal] = useState(false);
  const [isAttemptingDeepLink, setIsAttemptingDeepLink] = useState(true);

  useEffect(() => {
    const attemptRedirect = async () => {
      // Get livestream parameters
      const livestreamId = searchParams.get('id');
      const showType = searchParams.get('type');
      
      if (!livestreamId) {
        // No ID provided, show modal immediately
        setIsAttemptingDeepLink(false);
        setShowModal(true);
        return;
      }

      // Construct the deep link path and parameters
      const path = 'livestream/feed';
      const params = new URLSearchParams();
      params.set('id', livestreamId);
      if (showType) params.set('type', showType);
      params.set('itmSource', 'share');

      // Attempt to open the mobile app (mobile and desktop)
      await handleDeepLinkWithFallback(
        path,
        params,
        () => {
          // App not installed or user is on desktop - show modal
          setIsAttemptingDeepLink(false);
          setShowModal(true);
        }
      );

      // After the deep link attempt completes, if we're still here (app didn't open)
      // Show the modal regardless of device type
      // This ensures the modal appears on mobile when app is not installed
      setTimeout(() => {
        setIsAttemptingDeepLink(false);
        setShowModal(true);
      }, 2600); // Slightly longer than the deep link timeout (2500ms)
    };

    attemptRedirect();
  }, [searchParams]);

  return (
    <>
      {/* Loading state while attempting deep link */}
      {isAttemptingDeepLink && (
        <div className="flex flex-col items-center justify-center min-h-[calc(100dvh-75px)] md:min-h-[calc(100dvh-85px)]">
          <LoadingSpinner />
          <p className="mt-4 text-gray-600 font-lexend">Opening livestream...</p>
        </div>
      )}


      <AppDownloadModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title="Watch Live on Auqli"
        description="This livestream is available in our mobile app. Download now to watch!"
      />
    </>
  );
}
